import React, { useState, useEffect, useRef } from "react";
import { Search, Calendar, ChevronDown } from "lucide-react";

const AddPurchaseModal = ({ onSave, onCancel, purchase = null }) => {
  const [form, setForm] = useState({
    vendor: purchase?.vendors || "",
    product: purchase?.product || "",
    category: purchase?.category || "",
    date: purchase?.date ? new Date(purchase.date).toISOString().split('T')[0] : "",
    buyPrice: purchase?.buyPrice || "",
    sellPrice: purchase?.sellPrice || "",
    quantity: purchase?.quantity || "",
    uniqueId: purchase?.uniqueId || "",
    status: purchase?.status || "Active",
  });

  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [vendorSearch, setVendorSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const vendorDropdownRef = useRef(null);
  const productDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);

  // Sample data for dropdowns
  const vendors = [
    "Tech Solutions Inc.",
    "Global Supply Co.",
    "Premium Products Ltd.",
    "Digital Mart",
    "Vineo Lotus Ltd.",
  ];

  const products = [
    "iPhone 15 Pro",
    "Samsung Galaxy S24",
    "MacBook Pro M3",
    "iPad Air",
    "AirPods Pro",
    "Apple Watch Series 9",
    "Dell XPS 13",
    "Surface Pro 9",
  ];

  const categories = [
    "Mobile Phones",
    "Laptops",
    "Tablets",
    "Accessories",
    "Wearables",
    "Audio",
    "Chargers",
    "Cases & Covers",
  ];

  const statusOptions = ["Active", "Inactive"];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  const filteredVendors = vendors.filter((vendor) =>
    vendor.toLowerCase().includes(vendorSearch.toLowerCase())
  );

  const filteredProducts = products.filter((product) =>
    product.toLowerCase().includes(productSearch.toLowerCase())
  );

  const filteredCategories = categories.filter((category) =>
    category.toLowerCase().includes(categorySearch.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(event.target)) {
        setShowVendorDropdown(false);
      }
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target)) {
        setShowProductDropdown(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="add-purchase-modal-overlay">
      <div className="add-purchase-modal noscrollbar">
        <div className="add-purchase-modal-header">
          <h2 className="add-purchase-modal-title">
            {purchase ? "Edit Purchase" : "Add New Purchase"}
          </h2>
          <p className="add-purchase-modal-subtitle">
            {purchase ? "Update purchase information" : "Record a new purchase transaction"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="add-purchase-modal-form">
          <div className="add-purchase-modal-body">
            {/* Supplier Dropdown */}
            <div className="form-group">
              <label className="form-label">Supplier *</label>
              <div className="dropdown-container" ref={vendorDropdownRef}>
                <div
                  className="dropdown-input"
                  onClick={() => setShowVendorDropdown(!showVendorDropdown)}
                >
                  <span
                    className={
                      form.vendor ? "selected-value" : "not-selected-value"
                    }
                  >
                    {form.vendor || "Select vendor"}
                  </span>
                  <ChevronDown size={16} className="dropdown-arrow" />
                </div>
                {showVendorDropdown && (
                  <div className="dropdown-menu noscrollbar">
                    <div className="dropdown-search">
                      <Search size={16} className="purchases-search-icon" />
                      <input
                        type="text"
                        placeholder="Search vendor name..."
                        value={vendorSearch}
                        onChange={(e) => setVendorSearch(e.target.value)}
                        className="purchases-search-input full-width"
                        autoComplete="off"
                      />
                    </div>
                    <div className="dropdown-list noscrollbar">
                      {filteredVendors.map((vendor, index) => (
                        <div
                          key={index}
                          className="purchases-dropdown-item"
                          onClick={() => {
                            setForm((prev) => ({ ...prev, vendor }));
                            setShowVendorDropdown(false);
                            setVendorSearch("");
                          }}
                        >
                          {vendor}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Product Dropdown */}
            <div className="form-group">
              <label className="form-label">Product *</label>
              <div className="dropdown-container" ref={productDropdownRef}>
                <div className="dropdown-input" onClick={() => setShowProductDropdown(!showProductDropdown)}>
                  <span className={form.product ? "selected-value" : "not-selected-value"}>
                    {form.product || "Select product"}
                  </span>
                  <ChevronDown size={16} className="dropdown-arrow" />
                </div>
                {showProductDropdown && (
                  <div className="dropdown-menu noscrollbar">
                    <div className="dropdown-search">
                      <Search size={16} className="purchases-search-icon" />
                      <input type="text" placeholder="Search product name..." value={productSearch} onChange={(e) => setProductSearch(e.target.value)} className="purchases-search-input full-width" autoComplete="off" />
                    </div>
                    <div className="dropdown-list noscrollbar">
                      {filteredProducts.map((product, index) => (
                        <div key={index} className="purchases-dropdown-item" onClick={() => { setForm((prev) => ({ ...prev, product })); setShowProductDropdown(false); setProductSearch(""); }}>{product}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Category Dropdown */}
            <div className="form-group">
              <label className="form-label">Category *</label>
              <div className="dropdown-container" ref={categoryDropdownRef}>
                <div
                  className="dropdown-input"
                  onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                >
                  <span
                    className={
                      form.category ? "selected-value" : "not-selected-value"
                    }
                  >
                    {form.category || "Select category"}
                  </span>
                  <ChevronDown size={16} className="dropdown-arrow" />
                </div>
                {showCategoryDropdown && (
                  <div className="dropdown-menu noscrollbar">
                    <div className="dropdown-search">
                      <Search size={16} className="purchases-search-icon" />
                      <input
                        type="text"
                        placeholder="Search category..."
                        value={categorySearch}
                        onChange={(e) => setCategorySearch(e.target.value)}
                        className="purchases-search-input full-width"
                        autoComplete="off"
                      />
                    </div>
                    <div className="dropdown-list noscrollbar">
                      {filteredCategories.map((category, index) => (
                        <div
                          key={index}
                          className="purchases-dropdown-item"
                          onClick={() => {
                            setForm((prev) => ({ ...prev, category }));
                            setShowCategoryDropdown(false);
                            setCategorySearch("");
                          }}
                        >
                          {category}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Date Input */}
            <div className="form-group">
              <label className="form-label">Date *</label>
              <div className="date-input-container">
                <Calendar size={16} className="date-icon" />
                <input
                  type="date"
                  className="date-input no-right-icon"
                  name="date"
                  value={form.date}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            {/* Amount and Quantity Row */}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Buy Price *</label>
                <input
                  type="number"
                  className="form-input"
                  name="buyPrice"
                  value={form.buyPrice}
                  onChange={handleChange}
                  placeholder="€0.00"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Sell Price *</label>
                <input
                  type="number"
                  className="form-input"
                  name="sellPrice"
                  value={form.sellPrice}
                  onChange={handleChange}
                  placeholder="€0.00"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Quantity *</label>
                <input
                  type="number"
                  className="form-input"
                  name="quantity"
                  value={form.quantity}
                  onChange={handleChange}
                  placeholder="0"
                  required
                />
              </div>
            </div>

            {/*Unique Id*/}
            <div className="form-group">
              <label className="form-label">Unique Id *</label>
              <input
                type="text"
                className="form-input"
                name="uniqueId"
                value={form.uniqueId}
                onChange={handleChange}
                placeholder="01"
                required
              />
            </div>

            {/* Status */}
            <div className="form-group">
              <label className="form-label">Status *</label>
              <div className="dropdown-container" ref={statusDropdownRef}>
                <div
                  className="dropdown-input"
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                >
                  <span
                    className={form.status ? "selected-value" : "placeholder"}
                  >
                    {form.status || "Select"}
                  </span>
                  <ChevronDown size={16} className="dropdown-arrow" />
                </div>
                {showStatusDropdown && (
                  <div className="dropdown-menu">
                    <div className="dropdown-list">
                      {statusOptions.map((status, index) => (
                        <div
                          key={index}
                          className="dropdown-item"
                          onClick={() => {
                            setForm((prev) => ({ ...prev, status }));
                            setShowStatusDropdown(false);
                          }}
                        >
                          {status}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="add-purchase-modal-footer">
            <button type="button" className="brand-cancel-btn" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="brand-submit-btn">
              {purchase ? "Update Purchase" : "Add Purchase"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPurchaseModal;
